import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import { api, AdminCheckUserRow, AdminCheckUsersResponse } from '../../services/api';

const MAX_CEAS = 5000;

/** Rolling new-launch-close windows offered in the period selector. */
const MONTH_OPTIONS = [
  { months: 1, label: 'Last 1 month' },
  { months: 3, label: 'Last 3 months' },
  { months: 6, label: 'Last 6 months' },
  { months: 12, label: 'Last 1 year' },
];

const cardSx = {
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  boxShadow: 'none',
  p: 2,
};

const headCellSx = {
  fontWeight: 600,
  color: 'rgba(var(--color-text-rgb), 0.85)',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--color-text-strong)' },
} as const;

/** Computed column keys. Uploaded columns sort by their index instead. */
type ComputedField = 'recent' | 'window' | 'resale' | 'result';
type SortKey = { kind: 'source'; index: number } | { kind: 'computed'; field: ComputedField };
type SortDir = 'asc' | 'desc';
type ResultTab = 'all' | 'found' | 'notFound';

/** One uploaded/pasted row, kept in file order with its original cells intact. */
interface SourceRow {
  cells: string[];
  cea: string;
}

interface ParsedInput {
  columns: string[];
  rows: SourceRow[];
  /** True when the columns were synthesized from a plain CEA paste. */
  synthesized: boolean;
}

function cellToString(cell: unknown): string {
  if (cell == null || cell === '') return '';
  if (cell instanceof Date) return cell.toISOString();
  return String(cell).trim();
}

function isExcelFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type.includes('spreadsheet') ||
    file.type.includes('excel')
  );
}

/** Split a CSV/plain-text paste into rows of cells. Handles quoted CSV fields. */
function parseDelimitedRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',' || ch === ';' || ch === '\t') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  row.push(field);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

async function parseSpreadsheetRows(file: File): Promise<string[][]> {
  if (isExcelFile(file)) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) throw new Error('Excel file has no worksheets');
    const raw = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
      header: 1,
      raw: false,
      defval: '',
    });
    return raw
      .map((row) => (Array.isArray(row) ? row : []).map(cellToString))
      .filter((row) => row.some((c) => c.trim() !== ''));
  }
  return parseDelimitedRows(await file.text());
}

/** Header labels that mean "this column holds the CEA number". */
function looksLikeCeaHeader(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized.includes('cea') || normalized === 'reg no' || normalized === 'reg no.';
}

/**
 * Turn parsed spreadsheet rows into a column list plus one SourceRow per data row,
 * keeping every uploaded column so the results table can echo the original file.
 * Row 0 is treated as a header when one of its cells names a CEA column; otherwise
 * the columns are labelled generically and every row is treated as data.
 */
function toParsedInput(rows: string[][]): ParsedInput {
  if (rows.length === 0) return { columns: [], rows: [], synthesized: false };

  const headerCandidate = rows[0];
  const headerCeaIndex = headerCandidate.findIndex(looksLikeCeaHeader);
  const hasHeader = headerCeaIndex >= 0;

  const bodyRows = hasHeader ? rows.slice(1) : rows;
  const width = Math.max(headerCandidate.length, ...bodyRows.map((row) => row.length), 1);

  const columns = Array.from({ length: width }, (_, i) =>
    hasHeader ? headerCandidate[i]?.trim() || `Column ${i + 1}` : `Column ${i + 1}`,
  );

  // With no header to point at the CEA column, fall back to the first column that
  // holds a value on the first data row.
  const ceaIndex = hasHeader
    ? headerCeaIndex
    : Math.max(
        0,
        (bodyRows[0] || []).findIndex((cell) => cell.trim() !== ''),
      );

  const parsedRows = bodyRows
    .map((row) => ({
      cells: Array.from({ length: width }, (_, i) => (row[i] ?? '').trim()),
      cea: (row[ceaIndex] ?? '').trim().toUpperCase(),
    }))
    .filter((row) => row.cea !== '');

  return { columns, rows: parsedRows, synthesized: false };
}

/** A free-text paste becomes a CEA column; name and mobile are filled from the lookup. */
function parsePastedInput(text: string): ParsedInput {
  const ceas = text
    .split(/[\s,;]+/)
    .map((token) => token.trim().toUpperCase())
    .filter((token) => token !== '' && !looksLikeCeaHeader(token));
  return {
    columns: ['CEA No.', 'Name', 'Mobile'],
    rows: ceas.map((cea) => ({ cells: [cea, '', ''], cea })),
    synthesized: true,
  };
}

const CheckUserTab: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pastedText, setPastedText] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [months, setMonths] = useState<number>(12);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState<ParsedInput | null>(null);
  const [result, setResult] = useState<AdminCheckUsersResponse | null>(null);
  const [search, setSearch] = useState<string>('');
  const [resultTab, setResultTab] = useState<ResultTab>('all');
  const [sortKey, setSortKey] = useState<SortKey>({ kind: 'computed', field: 'window' });
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const pendingCount = useMemo(
    () => new Set(parsePastedInput(pastedText).rows.map((row) => row.cea)).size,
    [pastedText],
  );

  const runCheck = useCallback(async (parsed: ParsedInput, windowMonths: number) => {
    const uniqueCeas = Array.from(new Set(parsed.rows.map((row) => row.cea))).filter(Boolean);
    if (uniqueCeas.length === 0) {
      setError('No CEA numbers found in the input.');
      return;
    }
    if (uniqueCeas.length > MAX_CEAS) {
      setError(`Too many CEA numbers (${uniqueCeas.length}). The limit is ${MAX_CEAS} per check.`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.checkAdminUsers(uniqueCeas, windowMonths);
      setInput(parsed);
      setResult(response);
      setResultTab('all');
      setSearch('');
    } catch (err) {
      setResult(null);
      setError((err as Error)?.message || 'Failed to check users');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      setFileName(file.name);
      setError(null);
      try {
        const parsed = toParsedInput(await parseSpreadsheetRows(file));
        setPastedText('');
        await runCheck(parsed, months);
      } catch (err) {
        setResult(null);
        setError((err as Error)?.message || 'Could not read that file');
      }
    },
    [months, runCheck],
  );

  // Re-running on a period change keeps the table and its header labels in step.
  const handleMonthsChange = useCallback(
    (value: number) => {
      setMonths(value);
      if (input) void runCheck(input, value);
    },
    [input, runCheck],
  );

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      const same =
        key.kind === 'source'
          ? prev.kind === 'source' && prev.index === key.index
          : prev.kind === 'computed' && prev.field === key.field;
      if (same) {
        setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key.kind === 'source' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const lookupByCea = useMemo(() => {
    const map = new Map<string, AdminCheckUserRow>();
    for (const row of result?.rows ?? []) map.set(row.ceaNumber, row);
    return map;
  }, [result]);

  const columns = useMemo(() => input?.columns ?? [], [input]);

  /** Each uploaded row joined to its lookup result, in file order. */
  const joinedRows = useMemo(() => {
    if (!input || !result) return [];
    return input.rows.map((row, index) => {
      const lookup = lookupByCea.get(row.cea);
      // A plain paste has no name/mobile of its own, so borrow them from the lookup.
      const cells = input.synthesized
        ? [row.cea, lookup?.name || '', lookup?.mobile || '']
        : row.cells;
      return { key: `${row.cea}-${index}`, cells, cea: row.cea, lookup };
    });
  }, [input, lookupByCea, result]);

  const filteredSortedRows = useMemo(() => {
    let list = joinedRows;
    if (resultTab === 'found') list = list.filter((row) => row.lookup?.found);
    if (resultTab === 'notFound') list = list.filter((row) => !row.lookup?.found);

    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((row) =>
        [...row.cells, row.lookup?.name ?? '', row.lookup?.mobile ?? ''].some((value) =>
          value.toLowerCase().includes(term),
        ),
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortKey.kind === 'source') {
        const av = a.cells[sortKey.index] ?? '';
        const bv = b.cells[sortKey.index] ?? '';
        const an = Number(av);
        const bn = Number(bv);
        if (av !== '' && bv !== '' && Number.isFinite(an) && Number.isFinite(bn)) {
          return (an - bn) * dir;
        }
        return av.localeCompare(bv) * dir;
      }
      const rank = (row: typeof a): number => {
        if (sortKey.field === 'recent') return row.lookup?.closeNewLaunchRecent ?? -1;
        if (sortKey.field === 'window') return row.lookup?.closeNewLaunchWindow ?? -1;
        if (sortKey.field === 'resale') return row.lookup?.isResaleAgent ? 1 : 0;
        return row.lookup?.found ? 1 : 0;
      };
      return (rank(a) - rank(b)) * dir || a.cea.localeCompare(b.cea);
    });
  }, [joinedRows, resultTab, search, sortKey, sortDir]);

  const recentLabel = useMemo(() => {
    const active = result?.recentRange.months ?? months;
    const option = MONTH_OPTIONS.find((o) => o.months === active);
    return option ? option.label.toLowerCase() : `last ${active} months`;
  }, [months, result]);

  const windowLabel = result
    ? `${result.newLaunchWindow.startDate.slice(2, 7).replace('-', '/')}-${result.newLaunchWindow.endDate.slice(2, 7).replace('-', '/')}`
    : '03/25-03/26';

  const foundCount = useMemo(
    () => joinedRows.filter((row) => row.lookup?.found).length,
    [joinedRows],
  );
  const notFoundCount = joinedRows.length - foundCount;

  const handleExport = useCallback(
    (format: 'xlsx' | 'csv') => {
      if (!result || !input) return;

      const header = [
        ...columns,
        `NL Close (${recentLabel})`,
        `NL Close (${windowLabel})`,
        'Resale Agent (JY rule)',
        'Result',
      ];
      const body = filteredSortedRows.map((row) => [
        ...columns.map((_, i) => row.cells[i] ?? ''),
        row.lookup?.found ? row.lookup.closeNewLaunchRecent : '',
        row.lookup?.found ? row.lookup.closeNewLaunchWindow : '',
        row.lookup?.found ? (row.lookup.isResaleAgent ? 'Yes' : 'No') : '',
        row.lookup?.found ? 'Found' : 'Not found',
      ]);

      // Definitions travel with the data so the export stands on its own.
      const notes: Array<Array<string>> = [
        [],
        ['Definitions'],
        [
          `NL Close (${recentLabel})`,
          `New-launch transactions closed with an option date between ${result.recentRange.startDate} and ${result.recentRange.endDate}.`,
        ],
        [
          `NL Close (${windowLabel})`,
          `New-launch transactions closed with an option date between ${result.newLaunchWindow.startDate} and ${result.newLaunchWindow.endDate}.`,
        ],
        ['Resale Agent (JY rule)', result.resaleAgentRule],
        ['Result', 'Not found = no agent record matches this CEA number.'],
      ];

      const sheet = XLSX.utils.aoa_to_sheet([header, ...body, ...notes]);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Check Users');
      XLSX.writeFile(book, `check-users-${result.recentRange.endDate}.${format}`, {
        bookType: format,
      });
    },
    [columns, filteredSortedRows, input, recentLabel, result, windowLabel],
  );

  const summary = result?.summary;

  const sortIndicator = (key: SortKey) => {
    const active =
      key.kind === 'source'
        ? sortKey.kind === 'source' && sortKey.index === key.index
        : sortKey.kind === 'computed' && sortKey.field === key.field;
    if (!active) return null;
    return sortDir === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
    );
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.68)' }}>
        Upload or paste a list of CEA numbers to look up each agent&apos;s new-launch closes and
        whether they match the Resale Agent (JY rule). Every column in the uploaded file is kept
        and shown alongside the results; CEA numbers with no matching agent are listed as{' '}
        <strong>Not found</strong>.
      </Typography>

      <Card sx={cardSx}>
        <CardContent sx={{ p: '0 !important' }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) void handleFile(file);
                }}
              />
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                Upload CEA list
              </Button>
              <Button
                variant="outlined"
                onClick={() => void runCheck(parsePastedInput(pastedText), months)}
                disabled={loading || pendingCount === 0}
                sx={{
                  borderColor: 'rgba(var(--color-text-rgb), 0.18)',
                  color: 'rgba(var(--color-text-rgb), 0.88)',
                }}
              >
                Check pasted {pendingCount > 0 ? `(${pendingCount})` : ''}
              </Button>
              <TextField
                select
                size="small"
                label="NL close period"
                value={months}
                onChange={(event) => handleMonthsChange(Number(event.target.value))}
                disabled={loading}
                sx={{ minWidth: 170 }}
              >
                {MONTH_OPTIONS.map((option) => (
                  <MenuItem key={option.months} value={option.months}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {fileName ? (
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                  {fileName}
                </Typography>
              ) : null}
            </Box>

            <TextField
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              size="small"
              label="Or paste CEA numbers"
              placeholder={'R012935Z\nR058265H\nHT260114'}
              value={pastedText}
              onChange={(event) => setPastedText(event.target.value)}
              helperText="Upload a CSV/Excel to keep all of its columns, or paste one CEA per line for a plain CEA / name / mobile table."
            />
          </Stack>
        </CardContent>
      </Card>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !result || !input ? null : (
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={4}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: '0 !important' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    Rows Checked
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {joinedRows.length}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}
                  >
                    {summary && summary.unique !== joinedRows.length
                      ? `${summary.unique} unique CEA numbers`
                      : 'Unique CEA numbers'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={4}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: '0 !important' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    Found
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: '#38B2AC' }}>
                    {foundCount}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}
                  >
                    Matched to an agent record
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: '0 !important' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    Not Found
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mt: 0.5,
                      color: notFoundCount > 0 ? '#E53E3E' : undefined,
                    }}
                  >
                    {notFoundCount}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}
                  >
                    No agent with this CEA number
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{ alignItems: 'flex-start' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              How these columns are worked out
            </Typography>
            <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
              <strong>NL Close ({recentLabel})</strong> — new-launch transactions with an option
              date between {result.recentRange.startDate} and {result.recentRange.endDate}.
              <br />
              <strong>NL Close ({windowLabel})</strong> — new-launch transactions with an option
              date between {result.newLaunchWindow.startDate} and {result.newLaunchWindow.endDate}.
              <br />
              {result.resaleAgentRule}
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tabs
              value={resultTab}
              onChange={(_, value) => setResultTab(value as ResultTab)}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}
            >
              <Tab value="all" label={`All (${joinedRows.length})`} />
              <Tab value="found" label={`Found (${foundCount})`} />
              <Tab value="notFound" label={`Not Found (${notFoundCount})`} />
            </Tabs>
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small"
              placeholder="Filter rows…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('xlsx')}
              disabled={filteredSortedRows.length === 0}
              sx={{
                borderColor: 'rgba(var(--color-text-rgb), 0.18)',
                color: 'rgba(var(--color-text-rgb), 0.88)',
              }}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
              disabled={filteredSortedRows.length === 0}
              sx={{
                borderColor: 'rgba(var(--color-text-rgb), 0.18)',
                color: 'rgba(var(--color-text-rgb), 0.88)',
              }}
            >
              CSV
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={`${column}-${index}`}
                      sx={headCellSx}
                      onClick={() => handleSort({ kind: 'source', index })}
                    >
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {column}
                        {sortIndicator({ kind: 'source', index })}
                      </Box>
                    </TableCell>
                  ))}
                  {([
                    { field: 'recent' as const, label: `NL Close (${recentLabel})` },
                    { field: 'window' as const, label: `NL Close (${windowLabel})` },
                    { field: 'resale' as const, label: 'Resale Agent (JY rule)' },
                    { field: 'result' as const, label: 'Result' },
                  ] as const).map((col) => (
                    <TableCell
                      key={col.field}
                      align="right"
                      sx={{
                        ...headCellSx,
                        borderLeft:
                          col.field === 'recent'
                            ? '1px solid rgba(var(--color-text-rgb), 0.12)'
                            : undefined,
                      }}
                      onClick={() => handleSort({ kind: 'computed', field: col.field })}
                    >
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {col.label}
                        {sortIndicator({ kind: 'computed', field: col.field })}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 4}
                      sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}
                    >
                      No rows in this category.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSortedRows.map((row) => {
                    const found = Boolean(row.lookup?.found);
                    return (
                      <TableRow key={row.key} sx={!found ? { opacity: 0.75 } : undefined}>
                        {columns.map((column, index) => (
                          <TableCell key={`${row.key}-${index}`} sx={{ whiteSpace: 'nowrap' }}>
                            {row.cells[index]?.trim() ? row.cells[index] : '—'}
                          </TableCell>
                        ))}
                        <TableCell
                          align="right"
                          sx={{ borderLeft: '1px solid rgba(var(--color-text-rgb), 0.12)' }}
                        >
                          {found ? row.lookup?.closeNewLaunchRecent : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {found ? row.lookup?.closeNewLaunchWindow : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {!found ? (
                            '—'
                          ) : row.lookup?.isResaleAgent ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#38B2AC' }}>
                              Yes
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}
                            >
                              No
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {found ? (
                            <Chip
                              size="small"
                              label="Found"
                              sx={{
                                bgcolor: 'rgba(56, 178, 172, 0.16)',
                                color: '#38B2AC',
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Not found"
                              sx={{
                                bgcolor: 'rgba(229, 62, 62, 0.16)',
                                color: '#E53E3E',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Stack>
  );
};

export default CheckUserTab;
